<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Request ID Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.request_id')"
                v-model="filterOptions.request_id"
              />
              <!-- End:: Request ID Input -->

              <!-- Start:: User Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.user_name')"
                v-model="filterOptions.user_name"
              />
              <!-- End:: User Name Input -->

              <!-- Start:: Distribution Point Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.distribution_point_name')"
                v-model="filterOptions.distribution_point_name"
              />
              <!-- End:: Distribution Point Name Input -->

              <!-- Start:: Status Select -->
              <base-select-input
                col="3"
                :optionsList="statusOptions"
                :placeholder="$t('PLACEHOLDERS.request_status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Select -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.distribution_points_withdrawal_requests") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serial`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Request ID -->
        <template v-slot:[`item.id`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.id }}</p>
          </div>
        </template>
        <!-- End:: Request ID -->

        <!-- Start:: User Name -->
        <template v-slot:[`item.user_name`]="{ item }">
          <div class="table_text_wrapper">
            <h6 class="text-danger" v-if="!item.user || !item.user.name">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>{{ item.user.name }}</p>
          </div>
        </template>
        <!-- End:: User Name -->

        <!-- Start:: Distribution Point Name -->
        <template v-slot:[`item.distribution_point_name`]="{ item }">
          <div class="table_text_wrapper">
            <h6 class="text-danger" v-if="!item.user || !item.user.name">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>{{ item.user.name }}</p>
          </div>
        </template>
        <!-- End:: Distribution Point Name -->

        <!-- Start:: Amount -->
        <template v-slot:[`item.amount`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.amount }} {{ $t("PLACEHOLDERS.riyal") }}</p>
          </div>
        </template>
        <!-- End:: Amount -->

        <!-- Start:: Request Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.created_at }}</p>
          </div>
        </template>
        <!-- End:: Request Date -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <div class="table_text_wrapper">
            <span
              class="badge"
              :class="{
                'badge-warning': item.status === 'pending',
                'badge-success': item.status === 'approved',
                'badge-danger': item.status === 'rejected',
              }"
            >
              {{ getStatusText(item.status) }}
            </span>
          </div>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <!-- Show Details -->
          <a-tooltip placement="bottom">
            <template slot="title">
              <span>{{ $t("BUTTONS.show") }}</span>
            </template>
            <button class="btn_show" @click="showItemDetails(item)">
              <i class="fal fa-eye"></i>
            </button>
          </a-tooltip>

          <!-- Approve Request -->
          <a-tooltip placement="bottom" v-if="item.status === 'pending'">
            <template slot="title">
              <span>{{ $t("BUTTONS.approve") }}</span>
            </template>
            <button class="btn_accept" @click="showApproveModal(item)">
              <i class="fal fa-check"></i>
            </button>
          </a-tooltip>

          <!-- Reject Request -->
          <a-tooltip placement="bottom" v-if="item.status === 'pending'">
            <template slot="title">
              <span>{{ $t("BUTTONS.reject") }}</span>
            </template>
            <button class="btn_delete" @click="showRejectModal(item)">
              <i class="fal fa-times"></i>
            </button>
          </a-tooltip>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Details Modal -->
    <v-dialog v-model="detailsModal" max-width="600px">
      <v-card>
        <v-card-title class="modal_title">
          <h3>{{ $t("PLACEHOLDERS.request_details") }}</h3>
        </v-card-title>
        <v-card-text v-if="selectedItem">
          <div class="details_wrapper">
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.request_id") }}:</strong>
              <span>{{ selectedItem.id }}</span>
            </div>
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.user_name") }}:</strong>
              <span>{{ selectedItem.user.name || "-" }}</span>
            </div>
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.distribution_point_name") }}:</strong>
              <span>{{ selectedItem.user.name || "-" }}</span>
            </div>
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.withdrawal_amount") }}:</strong>
              <span
                >{{ selectedItem.amount }} {{ $t("PLACEHOLDERS.riyal") }}</span
              >
            </div>
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.bank_name") }}:</strong>
              <span>{{ selectedItem.bank_name }}</span>
            </div>
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.iban") }}:</strong>
              <span>{{ selectedItem.iban }}</span>
            </div>
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.request_date") }}:</strong>
              <span>{{ selectedItem.created_at }}</span>
            </div>
            <div class="detail_row">
              <strong>{{ $t("PLACEHOLDERS.request_status") }}:</strong>
              <span
                class="badge"
                :class="{
                  'badge-warning': selectedItem.status === 'pending',
                  'badge-success': selectedItem.status === 'approved',
                  'badge-danger': selectedItem.status === 'rejected',
                }"
              >
                {{ getStatusText(selectedItem.status) }}
              </span>
            </div>
            <div class="detail_row" v-if="selectedItem.status === 'rejected'">
              <strong>{{ $t("PLACEHOLDERS.rejection_reason") }}:</strong>
              <span>{{ selectedItem.admin_note || "-" }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- End:: Details Modal -->

    <!-- Start:: Approve Modal -->
    <v-dialog v-model="approveModal" max-width="500px">
      <v-card>
        <v-card-title class="modal_title">
          <h3>{{ $t("PLACEHOLDERS.approve_withdrawal_request") }}</h3>
        </v-card-title>
        <form>
          <v-card-text v-if="selectedItem">
            <div class="approve_form">
              <div class="form_group">
                <label>{{ $t("PLACEHOLDERS.requested_amount") }}</label>
                <p class="amount_display">
                  {{ selectedItem.amount }} {{ $t("PLACEHOLDERS.riyal") }}
                </p>
              </div>
              <div class="form_group">
                <base-input
                  type="number"
                  col="12"
                  :placeholder="$t('PLACEHOLDERS.sent_amount')"
                  v-model.trim="approveData.amount"
                  min="0"
                  :max="selectedItem.amount"
                  required
                />
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="modal_cancel_btn" @click="approveModal = false">
              {{ $t("BUTTONS.cancel") }}
            </v-btn>
            <v-btn
              class="modal_confirm_btn"
              @click="approveRequest"
              :loading="isWaitingRequest"
              :disabled="isWaitingRequest"
            >
              {{ $t("BUTTONS.save") }}
            </v-btn>
          </v-card-actions>
        </form>
      </v-card>
    </v-dialog>
    <!-- End:: Approve Modal -->

    <!-- Start:: Reject Modal -->
    <v-dialog v-model="rejectModal" max-width="500px">
      <v-card>
        <v-card-title class="modal_title">
          <h3>{{ $t("PLACEHOLDERS.reject_withdrawal_request") }}</h3>
        </v-card-title>
        <form>
          <v-card-text>
            <div class="reject_form">
              <base-input
                type="textarea"
                col="12"
                :placeholder="$t('PLACEHOLDERS.rejection_reason')"
                v-model.trim="rejectData.note"
                required
              />
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="modal_cancel_btn" @click="rejectModal = false">
              {{ $t("BUTTONS.cancel") }}
            </v-btn>
            <v-btn
              class="modal_confirm_btn"
              @click="rejectRequest"
              :loading="isWaitingRequest"
              :disabled="isWaitingRequest"
            >
              {{ $t("BUTTONS.save") }}
            </v-btn>
          </v-card-actions>
        </form>
      </v-card>
    </v-dialog>
    <!-- End:: Reject Modal -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllDistributionPointsWithdrawalRequests",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    statusOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.pending_status"),
          value: "pending",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.approved_status"),
          value: "approved",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.rejected_status"),
          value: "rejected",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        request_id: null,
        user_name: null,
        distribution_point_name: null,
        status: null,
      },
      // End:: Filter Data

      // Start:: Modals
      detailsModal: false,
      approveModal: false,
      rejectModal: false,
      selectedItem: null,
      // End:: Modals

      // Start:: Approve Data
      approveData: {
        amount: null,
      },
      // End:: Approve Data

      // Start:: Reject Data
      rejectData: {
        note: null,
      },
      // End:: Reject Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "serial",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.request_id"),
          value: "id",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.user_name"),
          value: "user_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.distribution_point_name"),
          value: "distribution_point_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.withdrawal_amount"),
          value: "amount",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.request_date"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.request_status"),
          value: "status",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    getStatusText(status) {
      const statusMap = {
        pending: this.$t("PLACEHOLDERS.pending_status"),
        approved: this.$t("PLACEHOLDERS.approved_status"),
        rejected: this.$t("PLACEHOLDERS.rejected_status"),
      };
      return statusMap[status] || status;
    },

    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/distribution-points-withdrawal-requests/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.request_id = null;
      this.filterOptions.user_name = null;
      this.filterOptions.distribution_point_name = null;
      this.filterOptions.status = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/distribution-points-withdrawal-requests/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;

      try {
        const params = {
          page: this.paginations.current_page,
        };

        // Add search filters only if they have values
        if (this.filterOptions.request_id) {
          params.id = this.filterOptions.request_id;
        }
        if (this.filterOptions.user_name) {
          params.user_name = this.filterOptions.user_name;
        }
        if (this.filterOptions.distribution_point_name) {
          params.search = this.filterOptions.distribution_point_name;
        }
        if (this.filterOptions.status?.value) {
          params.status = this.filterOptions.status.value;
        }

        let res = await this.$axios({
          method: "GET",
          url: "distrbution-points-withdrawal-requests",
          params: params,
        });

        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },

    showItemDetails(item) {
      this.selectedItem = item;
      this.detailsModal = true;
    },

    showApproveModal(item) {
      this.selectedItem = item;
      this.approveData.amount = item.amount;
      this.approveModal = true;
    },

    showRejectModal(item) {
      this.selectedItem = item;
      this.rejectData.note = null;
      this.rejectModal = true;
    },

    async approveRequest() {
      if (!this.approveData.amount || this.approveData.amount <= 0) {
        this.$message.error(this.$t("MESSAGES.pleaseEnterValidAmount"));
        return;
      }

      this.isWaitingRequest = true;

      try {
        await this.$axios({
          method: "POST",
          url: `distrbution-points-withdrawal-requests/${this.selectedItem.id}/approve`,
          data: {
            amount: this.approveData.amount,
          },
        });

        this.isWaitingRequest = false;
        this.approveModal = false;
        this.$message.success(this.$t("MESSAGES.approvedSuccessfully"));
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },

    async rejectRequest() {
      if (!this.rejectData.note || this.rejectData.note.trim() === "") {
        this.$message.error(this.$t("MESSAGES.pleaseEnterRejectionReason"));
        return;
      }

      this.isWaitingRequest = true;

      try {
        await this.$axios({
          method: "POST",
          url: `distrbution-points-withdrawal-requests/${this.selectedItem.id}/reject`,
          data: {
            note: this.rejectData.note,
          },
        });

        this.isWaitingRequest = false;
        this.rejectModal = false;
        this.$message.success(this.$t("MESSAGES.rejectedSuccessfully"));
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
  },
};
</script>

<style lang="scss" scoped>
.badge {
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;

  &.badge-success {
    background-color: #4caf50;
    color: white;
  }

  &.badge-danger {
    background-color: #f44336;
    color: white;
  }

  &.badge-warning {
    background-color: #ff9800;
    color: white;
  }

  &.badge-secondary {
    background-color: #9e9e9e;
    color: white;
  }
}

.modal_title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border_clr);

  h3 {
    margin: 0;
  }

  .close_btn {
    background: transparent;
    border: none;
    font-size: 20px;
    cursor: pointer;

    &:hover {
      color: var(--main_theme_clr);
    }
  }
}

.details_wrapper {
  padding: 10px 0;

  .detail_row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid var(--border_clr);

    &:last-child {
      border-bottom: none;
    }

    strong {
      font-weight: 600;
    }
  }
}

.approve_form,
.reject_form {
  padding: 10px 0;

  .form_group {
    margin-bottom: 20px;

    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
    }

    .amount_display {
      font-size: 24px;
      font-weight: bold;
      color: var(--main_theme_clr);
      margin: 0;
    }
  }
}

.btn_accept {
  background: transparent;
  border: none;
  color: #4caf50;
  font-size: 18px;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background: #4caf5020;
  }
}
</style>
